import {
    Box,
    CircularProgress,
    ListItem,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import {
    CircleCrossIcon,
    CircleTickIcon
} from "../../icons";

import { ConsolidatedDataSyncStatus } from "../../../lib/datastore/ConsolidatedDataSyncStatus";
import { SOFTWARE_VERSION } from "../../../lib/util/troubleshooting/SoftwareVersion";
import { consolidatedDataSyncStatusSelector } from "../../../lib/datastore/state/ConsolidatedDataSyncState";
import { isSystemReadySelector } from "../../../lib/system/state/SystemState";
import { useMemo } from "react";
import { useRecoilValue } from "recoil";

type DataSyncStatusIndicatorListItemProps = {
    isOpen: boolean;
};

export const DataSyncStatusIndicatorListItem = ({ isOpen }: DataSyncStatusIndicatorListItemProps) => {
    const dataSyncStatus = useRecoilValue<ConsolidatedDataSyncStatus>(consolidatedDataSyncStatusSelector);
    const isSystemReady = useRecoilValue<boolean>(isSystemReadySelector);
    const theme = useTheme();

    const dataSyncBackgroundColor = useMemo(() => {
        if (!isSystemReady) {
            return theme.palette.error.main;
        }
        switch (dataSyncStatus) {
            case ConsolidatedDataSyncStatus.SYNCING_FROM_CLOUD:
                return theme.palette.error.main;
            case ConsolidatedDataSyncStatus.SYNCING_TO_CLOUD:
                return theme.palette.warning.main;
            case ConsolidatedDataSyncStatus.NOTHING_TO_SYNC:
                return theme.palette.success.main;
            default:
                return "white";
        }
    }, [dataSyncStatus, isSystemReady, theme]);

    const dataSyncStatusTooltip = useMemo(() => {
        if (!isSystemReady) {
            return (
                <Tooltip title="System is not ready.">
                    <span>
                        <CircleCrossIcon primary="secondary" />
                    </span>
                </Tooltip>
            );
        }
        switch (dataSyncStatus) {
            case ConsolidatedDataSyncStatus.SYNCING_FROM_CLOUD:
                return (
                    <Tooltip title="System is not ready.">
                        <span>
                            <CircleCrossIcon primary="secondary" />
                        </span>
                    </Tooltip>
                );
            case ConsolidatedDataSyncStatus.SYNCING_TO_CLOUD:
                return (
                    <Tooltip title="Data sync in progress.">
                        <span>
                            <CircularProgress size={24} color="secondary" />
                        </span>
                    </Tooltip>
                );
            case ConsolidatedDataSyncStatus.NOTHING_TO_SYNC:
                return (
                    <Tooltip title="System is ready.">
                        <span>
                            <CircleTickIcon primary="secondary" />
                        </span>
                    </Tooltip>
                );
            default:
                return null;
        }
    }, [dataSyncStatus, isSystemReady]);


    return (
        <ListItem
            key="datastoreReadinessStatus"
            sx={{
                py: 0.5,
                px: 0,
                backgroundColor: isOpen ? dataSyncBackgroundColor : theme.palette.text.primary
            }}
        >
            <Box
                display="flex"
                flexDirection="column"
                width="100%"
                alignItems="center"
            >
                {isOpen && dataSyncStatusTooltip}
                <Typography variant="caption" color="secondary">
                    v{SOFTWARE_VERSION}
                </Typography>
            </Box>
        </ListItem>
    );
};